import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  CheckCircle2, 
  Shield, 
  Award, 
  Users, 
  Target,
  Heart,
  Phone
} from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import Header from './Header';
import Footer from './Footer';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Fiduciary Duty',
      description: 'We are legally bound to act in your best interest at all times. Your goals are our priority.'
    },
    {
      icon: Heart,
      title: 'Client-First Approach',
      description: 'We listen to your hopes and goals and propose customized strategies based on your unique situation.'
    },
    {
      icon: Target,
      title: 'Personalized Strategies',
      description: 'No cookie-cutter solutions. Every financial plan is tailored specifically to your needs and objectives.'
    },
    {
      icon: Award,
      title: 'Expertise & Experience',
      description: 'Decades of experience serving retirees, near-retirees, and business owners throughout Oklahoma.'
    }
  ];

  const services = [
    'Financial and Wealth Planning',
    'Investment Management',
    'Portfolio Management',
    'Retirement Income Planning',
    'Legacy Planning',
    'Tax Efficient Strategies',
    'Insurance Solutions',
    'Business Strategies'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] to-white" data-testid="about-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="hero-headline text-4xl sm:text-5xl font-bold text-[#3d4f5f] mb-6">
            About Summit Investment Group
          </h1>
          <p className="hero-subheadline text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            Helping you reach the summit of your financial life. We partner with retirees, near-retirees, and business owners to build, protect, and grow wealth through personalized strategies.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="our-story">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-6">
                  Our Mission
                </h2>
                <p className="text-gray-600 mb-6">
                  Do you have a desire to build wealth through investing, fund a child's education, plan for a comfortable retirement, or share a legacy? If so, Summit Investment Group can help you develop a personalized map to help reach your retirement goals.
                </p>
                <p className="text-gray-600 mb-6">
                  We listen to your hopes and goals and propose customized strategies based on your unique situation. Then, we evaluate along the way to make sure you are on track, no matter what twists and turns you experience on the road of life.
                </p>
                <p className="text-gray-600">
                  If you are a retiree, near-retiree or a business owner, we want to partner with you. Summit Investment Group offers personalized strategies for growing and protecting your wealth through investment strategies, planning for your family's future and your retirement income.
                </p>
              </div>
              <div className="relative">
                <img
                  src="/man-cropped.jpg"
                  alt="Financial planning team meeting"
                  className="rounded-2xl shadow-xl w-full h-auto object-cover"
                  style={{ aspectRatio: 1, objectPosition: "top" }}
                />
                <div className="absolute -bottom-6 -right-6 bg-[#3d4f5f] text-white p-6 rounded-xl shadow-lg hidden sm:block">
                  <p className="text-3xl font-bold">20+</p>
                  <p className="text-sm text-gray-200">Years of Experience</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Our Values Section */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]" data-testid="our-values">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-4">Our Values</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The principles that guide everything we do for our clients
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <Card key={index} className="card-hover p-8 bg-white text-center">
                  <div className="w-16 h-16 bg-[#e8eef2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-[#3d4f5f]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#3d4f5f] mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Overview */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="services-overview">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-4">Our Services</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive financial solutions tailored to your unique needs
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 p-4 bg-[#f8fafb] rounded-lg hover:bg-[#e8eef2] transition-colors"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#4a6274] flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{service}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/contact">
                <Button className="bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl">
                  Schedule a Consultation
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Who We Serve */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]" data-testid="who-we-serve">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-4">Who We Serve</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Expertise for retirees, near-retirees and business owners
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="card-hover p-8 bg-white">
                <Users className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">Retirees</h3>
                <p className="text-gray-600">
                  Secure your retirement income, protect your assets, and enjoy the lifestyle you've worked hard to achieve.
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <Target className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">Near-Retirees</h3>
                <p className="text-gray-600">
                  Plan your transition to retirement with confidence through strategic planning and smart decisions.
                </p>
              </Card>
              <Card className="card-hover p-8 bg-white">
                <Award className="w-12 h-12 text-[#3d4f5f] mb-4" />
                <h3 className="text-xl font-bold text-[#3d4f5f] mb-3">Business Owners</h3>
                <p className="text-gray-600">
                  Integrate your business and personal finances with strategies that grow both your company and your wealth.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Serving Oklahoma */}
      <ScrollReveal>
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="serving-area">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3d4f5f] mb-6">Serving Oklahoma</h2>
            <p className="text-lg text-gray-600 mb-8">
              Summit Investment Group is proud to serve clients throughout the Tulsa, Oklahoma City, and Edmond, Oklahoma area. Our deep understanding of the local economy and community allows us to provide financial guidance that's relevant to your life.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              {['Tulsa', 'Oklahoma City', 'Edmond', 'Broken Arrow', 'Norman', 'Owasso', 'Jenks', 'Bixby'].map((city) => (
                <span key={city} className="px-4 py-2 bg-[#f8fafb] text-[#3d4f5f] rounded-full border border-gray-200">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#3d4f5f]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Are you ready to take the next step on your financial journey?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Let's schedule a time to meet and discuss how we can help you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#3d4f5f] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-lg transition-all hover:shadow-xl">
                Schedule Now
              </Button>
            </Link>
            <a href="tel:+19184777227">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#3d4f5f] px-8 py-6 text-lg rounded-lg transition-all">
                <Phone className="w-5 h-5 mr-2" />
                918.477.7227
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-100" data-testid="disclaimer">
        <div className="max-w-4xl mx-auto text-center text-xs text-gray-500 space-y-4">
          <p>
            Investment advisory services are provided in accordance with a fiduciary duty of care and loyalty that includes putting your interests first and disclosing conflicts. Insurance services have a best interest standard which requires recommendations to be in your best interest.
          </p>
          <p>
            Investment advisory services offered through CreativeOne Wealth, LLC a Registered Investment Adviser. CreativeOne Wealth, LLC and Summit Investment Group, LLC are unaffiliated entities. Licensed Insurance Professional.
          </p>
          <p>
            Investing involves risk, including the loss of principal. No investment strategy can guarantee a profit or protect against loss in a period of declining values.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
